import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { AplicationService } from '@services/aplication/aplication.service';
import { Observable, throwError } from 'rxjs';
import { CampaignsRepository } from './campaigns.repository';
import { CreateCampaignDto } from './dtos/create-campaign.dto';
import { UpdateCampaignDto } from './dtos/update-campaign.dto';
import { CampaignStatus } from './types/campaign.type';
import { CampaignsPaginatedResponse, GetCampaignResponse } from '../domain/campaign.model';

@Injectable({
    providedIn: 'root',
})
export class CampaignsApiService implements CampaignsRepository {
    private readonly http = inject(HttpClient);
    private readonly aplicationService = inject(AplicationService);

    /**
     * Builds the roads-scoped base URL and invokes `request` with it. When no aplication
     * is selected, fails fast by returning an Observable error instead of throwing
     * synchronously, so callers' existing `catchError`/`finalize` pipelines still run.
     */
    private withCampaignsBaseUrl<T>(request: (baseUrl: string) => Observable<T>): Observable<T> {
        const roadId = this.aplicationService.selectedRoadId();

        if (!roadId) {
            return throwError(() => new Error('Cannot build campaigns request URL: no aplication selected'));
        }

        return request(`${environment.url}/prism-admin-campaigns/v2/roads/${roadId}/campaigns`);
    }

    createCampaign(request: CreateCampaignDto): Observable<void> {
        return this.withCampaignsBaseUrl((baseUrl) => this.http.post<void>(baseUrl, request));
    }

    getCampaigns(page = 1, limit = 10, status?: CampaignStatus): Observable<CampaignsPaginatedResponse> {
        let params = new HttpParams().set('page', page).set('limit', limit);

        if (status) {
            params = params.set('status', status);
        }

        return this.withCampaignsBaseUrl((baseUrl) => this.http.get<CampaignsPaginatedResponse>(baseUrl, { params }));
    }

    getCampaign(id: string): Observable<GetCampaignResponse> {
        return this.withCampaignsBaseUrl((baseUrl) => this.http.get<GetCampaignResponse>(`${baseUrl}/${id}`));
    }

    updateCampaign(id: string, request: UpdateCampaignDto): Observable<GetCampaignResponse> {
        return this.withCampaignsBaseUrl((baseUrl) => this.http.patch<GetCampaignResponse>(`${baseUrl}/${id}`, request));
    }
}
